// @flow
import React, {Component} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {injectIntl} from 'react-intl';
import classNames from 'classnames';
import FormModal from '../FormModal/FormModal';
import BedroomsFilter from '../BedroomsFilter/BedroomsFilter';
import BathroomsFilter from '../BathroomsFilter/BathroomsFilter';
import MoreFilters from '../MoreFilters/MoreFilters';
import InstantConfirmationFilter from '../InstantConfirmationFilter/InstantConfirmationFilter';
import PriceFilter from '../PriceFilter/PriceFilter';
import seeResultsMessage from '../SeeResultsMessage/SeeResultsMessage';
import {media} from '../../utils/breakpoints';

import {trigger as edapTrigger, GENERIC_EVENT} from '../../utils/edapWrapper';
import routerActions from '../../actions/router';
import searchParamsActions from '../../actions/searchParams';
import {cannotFilter} from '../../utils/searchParamsUtils';
import {instantBookFilter, newArrayFromFilters} from '../../utils/filters';

import type {State as ReduxState} from '../../reducers/index';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {BoundsType} from '../PriceFilter/PriceFilter';
import type {IntlShape} from 'react-intl';
import type {FilterGroupType} from '../MoreFilters/MoreFilters';
import {FILTERS_MODAL} from '../../actions/modals';

import './FilterForm.less';
import {petsInFilters} from '@homeaway/search-url-parser';
import {triggerEdapEventForCheckbox} from '../../utils/filtersEdapUtils';
import {HA_SERP_MIN_MAX_PRICE_FIELDS} from '../../abacus/experiments';
import {logExperiment} from '@homeaway/abacus-client-utils';
import {MIN_ALLOWED_WINDOW_HEIGHT_FOR_MODAL_FIXED_CTA} from '../../utils/constants';

const showMoreFilters = true;

type StateProps = {
    currency: string,
    hasDates: boolean,
    inputParams: SearchParamsType,
    inputParamsAreChanged: boolean,
    inputParamsAreDefault: boolean,
    isMobile: boolean,
    locale: string,
    minMaxPriceFieldsTest?: Object,
    modalIsOpen: boolean,
    priceInputValuesAreValid: boolean,
    viewportHeight: number
};

type DispatchProps = {
    filter: typeof routerActions.filter,
    syncInputParams: typeof searchParamsActions.syncInputParams,
    updateInputParams: typeof searchParamsActions.updateInputParams,
    resetInputParams: typeof searchParamsActions.resetInputParams,
    updateFilterCheck: typeof searchParamsActions.updateFilterCheck,
    onDone: () => void
};

type InjectIntlProps = {
    intl: IntlShape
};

type Props = {
    resultsCount?: ?number,
    filterGroups: FilterGroupType[]
} & StateProps &
    DispatchProps &
    InjectIntlProps;

// TODO: move handlers for shared logic between FilterForm and FilterBar
// https://jira.homeawaycorp.com/browse/SERP-909
export class FilterForm extends Component<Props> {
    componentDidUpdate = (prevProps: Props) => {
        if (this.props.modalIsOpen && !prevProps.modalIsOpen) {
            this.edapEvent('filters open');
        }
    };

    loggedMinMaxPriceFieldsTestFlag: boolean = false;

    edapEvent = (event: string) => {
        edapTrigger(GENERIC_EVENT, {
            eventaction: event
        });
    };

    handleResetForm = () => {
        this.edapEvent('all filters cleared');
        this.props.resetInputParams();
    };

    handleDiscardChanges = () => {
        const {syncInputParams} = this.props;
        syncInputParams();
    };

    handleOpen = () => {
        if (
            this.props.minMaxPriceFieldsTest &&
            !this.loggedMinMaxPriceFieldsTestFlag
        ) {
            logExperiment(this.props.minMaxPriceFieldsTest);
            this.loggedMinMaxPriceFieldsTestFlag = true;
        }
    };

    handleClose = () => {
        this.edapEvent('filters canceled');
        this.handleDiscardChanges();
        this.props.onDone();
    };

    handleSubmit = () => {
        const {filter, inputParams, onDone, inputParamsAreChanged} = this.props;

        // sync pets based on pet filters setting
        const petsApplied = petsInFilters(newArrayFromFilters(inputParams));
        if (inputParamsAreChanged) {
            this.edapEvent('filters applied');
            filter({
                ...inputParams,
                petIncluded: petsApplied
            });
        }

        onDone();
    };

    handlePriceChange = (bounds: BoundsType) => {
        const {inputParams, updateInputParams} = this.props;
        const values = {
            ...inputParams,
            minPrice: bounds[0] || 0,
            maxPrice: bounds[1]
        };
        updateInputParams(values);
    };

    handleRangeFilterChange = (params: SearchParamsType) => {
        const {updateInputParams} = this.props;
        const update = {
            ...params
        };
        updateInputParams(update);
    };

    handleMoreFilterCheck = (event: SyntheticEvent<HTMLInputElement>) => {
        if (!event || !event.currentTarget) {
            return;
        }

        triggerEdapEventForCheckbox(event);

        this.props.updateFilterCheck(event.currentTarget);
    };

    render() {
        const {
            handleOpen,
            handleClose,
            handleResetForm,
            handleSubmit,
            handlePriceChange,
            handleRangeFilterChange,
            handleMoreFilterCheck,
            props: {
                currency,
                filterGroups,
                hasDates,
                inputParams,
                inputParamsAreDefault,
                intl,
                isMobile,
                locale,
                modalIsOpen,
                priceInputValuesAreValid,
                resultsCount,
                viewportHeight
            }
        } = this;

        const isInstantConfirmationChecked = !!instantBookFilter(
            inputParams.filter
        );

        const isBodyCTA: boolean =
            viewportHeight < MIN_ALLOWED_WINDOW_HEIGHT_FOR_MODAL_FIXED_CTA;

        const submitDisabled =
            cannotFilter(resultsCount) || !priceInputValuesAreValid;

        return (
            <div className="FilterForm" data-wdio="FilterForm">
                <FormModal
                    modalId="filterFormModal"
                    isOpen={modalIsOpen}
                    submitDisabled={submitDisabled}
                    headingLabel={intl.formatMessage({
                        id: 'filtersDialogLabel',
                        defaultMessage: 'Filters'
                    })}
                    submitLabel={seeResultsMessage({intl, resultsCount})}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    onReset={handleResetForm}
                    onSubmit={handleSubmit}
                    resetVisible={
                        !inputParamsAreDefault || !priceInputValuesAreValid
                    }
                    isBodyCTA={isBodyCTA}
                >
                    <PriceFilter
                        currency={currency}
                        hasDates={hasDates}
                        onPriceChange={handlePriceChange}
                        minPrice={inputParams.minPrice}
                        maxPrice={inputParams.maxPrice}
                        locale={locale}
                        className="FilterForm__item"
                    />
                    <BedroomsFilter
                        onChange={handleRangeFilterChange}
                        minBedrooms={inputParams.minBedrooms || 0}
                        maxBedrooms={inputParams.maxBedrooms}
                        className="FilterForm__item"
                    />
                    <div
                        className={classNames(
                            'FilterForm__filter-section--bottom-bordered',
                            'FilterForm__filter-section'
                        )}
                    >
                        <BathroomsFilter
                            onChange={handleRangeFilterChange}
                            minBathrooms={inputParams.minBathrooms || 0}
                            maxBathrooms={inputParams.maxBathrooms}
                        />
                    </div>
                    <InstantConfirmationFilter
                        id="instantConfirmation__switch"
                        checked={isInstantConfirmationChecked}
                        handleChecked={handleMoreFilterCheck}
                        intl={intl}
                        className="FilterForm__item"
                    />
                    {showMoreFilters && filterGroups && (
                        <MoreFilters
                            checked={newArrayFromFilters(inputParams)}
                            handleChecked={handleMoreFilterCheck}
                            filterGroups={filterGroups}
                            tooltipOptions={{isMobile}}
                        />
                    )}
                </FormModal>
            </div>
        );
    }
}

export const mapStateToProps = (state: ReduxState): StateProps => {
    const {
        searchParams,
        system,
        modals,
        abacus,
        viewport: {height, breakpoint}
    } = state;
    const {
        params,
        inputParams,
        inputParamsAreDefault,
        inputParamsAreChanged,
        priceInputValuesAreValid
    } = searchParams;
    return {
        isMobile: !media(breakpoint, {minWidth: 'SM_MIN'}),
        bedroomsExactMatch: inputParams.minBedrooms === inputParams.maxBedrooms,
        currency: system.currency,
        locale: system.locale,
        modalIsOpen: modals.FILTERS.isOpen,
        hasDates: !!(inputParams.arrival && inputParams.departure),
        params,
        inputParams,
        inputParamsAreDefault,
        inputParamsAreChanged,
        priceInputValuesAreValid,
        minMaxPriceFieldsTest: abacus[HA_SERP_MIN_MAX_PRICE_FIELDS],
        viewportHeight: height
    };
};

const mapDispatchToProps = {
    filter: routerActions.filter,
    onDone: () => routerActions.popModal(FILTERS_MODAL),
    syncInputParams: searchParamsActions.syncInputParams,
    updateInputParams: searchParamsActions.updateInputParams,
    updateFilterCheck: searchParamsActions.updateFilterCheck,
    resetInputParams: searchParamsActions.resetInputParams
};

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    injectIntl
)(FilterForm);
